import { FC } from "react";
import { Box, Flex, Text } from "@radix-ui/themes";
import styled from "@emotion/styled";
import { ChevronDown, ChevronRight } from "lucide-react";
import { SidebarGroupItem } from "@/app/layout/types";
import { SidebarItemRow } from "@/app/layout/sidebar-item-row";
import { SIDEBAR_ITEM_HEIGHT } from "@/app/layout/constants";
import { BaseButton } from "@/components/button";

interface SidebarGroupProps {
  group: SidebarGroupItem;
  isCollapsed: boolean;
  onToggle: () => void;
  onItemSelect?: () => void;
}

export const SidebarGroup: FC<SidebarGroupProps> = ({
  group,
  isCollapsed,
  onToggle,
  onItemSelect,
}) => {
  return (
    <GroupBox>
      <GroupHeader onClick={onToggle}>
        <Flex align="center" gap="2">
          {group.icon && <group.icon size={16} />}
          <GroupLabel>{group.label}</GroupLabel>
        </Flex>
        {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
      </GroupHeader>
      {!isCollapsed && (
        <GroupItems>
          {group.items.map((item, index) => (
            <SidebarItemRow
              key={`${group.id}-${index}`}
              item={item}
              onSelect={onItemSelect}
              indentLevel={1}
            />
          ))}
        </GroupItems>
      )}
    </GroupBox>
  );
};

const GroupBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const GroupHeader = styled(BaseButton)`
  cursor: pointer;
  padding-left: 6px !important;
  padding-right: 12px !important;
  color: var(--gray-11);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border: none !important;
  box-shadow: none !important;
  height: ${SIDEBAR_ITEM_HEIGHT}px;
  min-height: ${SIDEBAR_ITEM_HEIGHT}px;
  margin: 0px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  background-color: transparent;
  border-radius: 4px !important;

  &:hover {
    background-color: var(--gray-3) !important;
  }
`;

const GroupLabel = styled(Text)`
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: var(--gray-11);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupItems = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;
